import { Component, Inject, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatTableModule } from '@angular/material/table';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { take } from 'rxjs/operators';
import { DocumentViewerResult, DocumentViewerService } from '../../../service/document-viewer.service';
import { DocumentAnalysisResponse, ExtractedParty, ExtractedField, ExtractedNoticePeriod, ExtractedOverview, ExtractedKeySections, KeySection } from '../../../service/ai/document-analysis.service';
import { Provision } from '../../../objects/provision';
import { CLAUSE_LABELS_BY_KEYSECTION, CLAUSE_SECTIONS } from '../../../objects/clause-sections';
import { ProvisionAnalysis, ProvisionComparison, ProvisionAnalysisRequestItem, ProvisionAnalysisResponse } from '../../../objects/provision-analysis';
import { ProvisionAnalysisService } from '../../../service/ai/provision-analysis.service';
import { ApprovedProvisionsService } from '../../../service/ai/approved-provisions.service';
import { ProvisionReviewTableComponent } from '../provision-review-table/provision-review-table.component';
import { ContractService } from '../../../service/entity-services/contract.service';
import { DatabaseAction } from '../../../objects/interface-library';

export interface AIAnalysisDialogData {
  contractName: string;
  contractId: string;
  contractType?: string;
  /** Durable, previously-saved provision gap analysis (contract.provisionAnalysis), if any. */
  contractProvisionAnalysis?: ProvisionAnalysis | null;
  document: DocumentViewerResult | null;
  analysisResponse?: DocumentAnalysisResponse | null;
  analysisError?: string;
  /** Called with the clause fields that "Save to Contract" wrote, so the host page can
   *  show them without a reload. */
  onClausesSaved?: (filled: { [contractField: string]: string }) => void;
}

/** A row in the Playbook tab, sourced from an approved provision that applies to
 *  this contract's type. */
export interface PlaybookItem {
  provName: string;
  contractTypes: string;      // joined for display
  recommendedLanguage: string;
}

export interface ContractOverview {
  contractParties: string;
  contractPurpose: string;
  effectiveDate: string;
  expirationDate: string;
  noticeToHospital: string;
  noticeToPhysician: string;
  term: string;
}


@Component({
  selector: 'app-ai-analysis-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatTabsModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatChipsModule,
    MatTableModule,
    MatDividerModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    ProvisionReviewTableComponent
  ],
  templateUrl: './ai-analysis-dialog.component.html',
  styleUrls: ['./ai-analysis-dialog.component.css']
})
export class AIAnalysisDialogComponent implements OnInit {
  private documentViewerService = inject(DocumentViewerService);
  private provisionAnalysisService = inject(ProvisionAnalysisService);
  private approvedProvisionsService = inject(ApprovedProvisionsService);
  private contractService = inject(ContractService);

  /** Playbook rows: approved provisions that apply to this contract's type. Populated in ngOnInit. */
  playbookItems: PlaybookItem[] = [];

  selectedTabIndex = 0;

  // AI Analysis data from API
  hasAIAnalysis = false;
  analysisError: string | null = null;
  extractedParties: ExtractedParty[] = [];
  extractedEffectiveDate: ExtractedField | null = null;
  extractedNoticePeriod: ExtractedNoticePeriod | null = null;
  analyzedAt: string | null = null;

  // Phase 2 AI Analysis fields
  extractedSummary: string | null = null;
  extractedOverview: ExtractedOverview | null = null;
  extractedKeySections: ExtractedKeySections | null = null;

  // Key sections display configuration — keyed off the AI keySections blob.
  // Sourced from the shared CLAUSE_SECTIONS registry (single source of truth).
  keySectionLabels: { [key: string]: string } = CLAUSE_LABELS_BY_KEYSECTION;

  /** The approved provisions that apply to this contract (shared by Playbook + Analysis). */
  applicableProvisions: Provision[] = [];

  /** Analysis tab: the durable, previously-saved gap-analysis comparisons (from the contract). */
  provisionComparisons: ProvisionComparison[] = [];
  provisionAnalyzedAt: string | null = null;

  /** Review tab: "Save to Contract" state. */
  clauseSaveState: 'idle' | 'saving' | 'saved' | 'nothing-to-add' | 'error' = 'idle';
  clauseSaveAdded: string[] = [];
  clauseSaveSkipped: string[] = [];

  /** Analysis tab run state. */
  provisionAnalysisRunning = false;
  provisionAnalysisStatus = '';
  provisionAnalysisError: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<AIAnalysisDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AIAnalysisDialogData
  ) {}

  ngOnInit(): void {
    this.processAnalysisResponse();
    // Analysis tab renders from the durable copy saved on the contract (if any).
    this.provisionComparisons = this.data.contractProvisionAnalysis?.comparisons || [];
    this.provisionAnalyzedAt = this.data.contractProvisionAnalysis?.analyzedAt || null;
    this.loadApplicableProvisions();
  }

  /**
   * Load the approved provisions that apply to this contract's type — the shared basis for
   * BOTH the Playbook tab (lists them) and the Analysis tab (sends them to the backend).
   * The rule itself lives in ApprovedProvisionsService, shared with the workflow request's
   * Document Review.
   */
  private loadApplicableProvisions(): void {
    this.approvedProvisionsService.applicableFor(this.data.contractType)
      .pipe(take(1))
      .subscribe(provisions => this.setApplicableProvisions(provisions));
  }

  private setApplicableProvisions(provisions: Provision[]): void {
    this.applicableProvisions = provisions;
    this.playbookItems = this.applicableProvisions.map(p => ({
      provName: p.provName || 'Unnamed Provision',
      contractTypes: (p.contractTypes || []).join(', '),
      recommendedLanguage: this.normalizeProvisionHtml(p.description || '')
    }));
  }

  /**
   * Provision language from the rich-text editor is stored with a non-breaking space
   * (&nbsp; / U+00A0) between every word, which never wraps — the cell renders as one
   * long horizontally-scrolling line. Convert those to normal spaces so it wraps as a
   * paragraph. Keeps real formatting tags (<strong>, <p>, ...) intact.
   */
  private normalizeProvisionHtml(html: string): string {
    if (!html) return '';
    return html
      .replace(/&nbsp;/gi, ' ')
      .replace(/\u00A0/g, ' ');
  }

  /**
   * The payload sent to the provision-analysis backend: one entry per applicable approved
   * provision. (Consumed when the Analysis trigger/Re-analyze call is wired up.)
   */
  getProvisionRequestPayload(): ProvisionAnalysisRequestItem[] {
    return this.approvedProvisionsService.toRequestItems(this.applicableProvisions);
  }

  getProvisionAnalyzedAtDisplay(): string {
    if (!this.provisionAnalyzedAt) return '';
    try {
      return new Date(this.provisionAnalyzedAt).toLocaleString();
    } catch {
      return this.provisionAnalyzedAt;
    }
  }

  /**
   * Run (or re-run) the AI gap analysis: send the applicable approved provisions to the
   * backend, poll to completion, render the comparisons, and persist them onto the contract
   * (frontend owns the write — the backend never touches the Contracts table).
   */
  runProvisionAnalysis(): void {
    if (this.provisionAnalysisRunning || this.applicableProvisions.length === 0) {
      return;
    }
    this.provisionAnalysisRunning = true;
    this.provisionAnalysisError = null;
    this.provisionAnalysisStatus = 'Starting analysis...';

    const payload = this.getProvisionRequestPayload();
    this.provisionAnalysisService
      .triggerProvisionAnalysis(this.data.contractId, payload, p => { this.provisionAnalysisStatus = p.message; })
      .pipe(take(1))
      .subscribe((resp: ProvisionAnalysisResponse) => {
        this.provisionAnalysisRunning = false;
        this.provisionAnalysisStatus = '';

        if (resp.status === 'complete' || resp.status === 'cached') {
          this.provisionComparisons = resp.comparisons || [];
          this.provisionAnalyzedAt = resp.analyzedAt || new Date().toISOString();
          this.persistAnalysis(resp);
        } else {
          this.provisionAnalysisError = resp.error || 'Analysis failed. Please try again.';
        }
      });
  }

  /**
   * Persist the completed analysis onto the contract (contract.provisionAnalysis).
   * Fetches the current contract, sets the field, and writes it back. Fire-and-forget:
   * a persistence failure doesn't block the already-rendered result.
   */
  private persistAnalysis(resp: ProvisionAnalysisResponse): void {
    const analysis: ProvisionAnalysis = {
      analyzedAt: resp.analyzedAt || new Date().toISOString(),
      provisionSetHash: (resp as any).provisionSetHash,
      comparisons: resp.comparisons || []
    };

    this.contractService.getContractById(this.data.contractId)
      .pipe(take(1))
      .subscribe(contract => {
        if (!contract) {
          console.warn('[ProvisionAnalysis] contract not found for persistence:', this.data.contractId);
          return;
        }
        contract.provisionAnalysis = analysis;
        this.contractService.writeContract(contract, 'active', DatabaseAction.Edit)
          .pipe(take(1))
          .subscribe({
            error: err => console.error('[ProvisionAnalysis] failed to persist onto contract:', err)
          });
      });
  }

  /** Clauses the AI found with language, i.e. what "Save to Contract" could write. */
  get foundClauseCount(): number {
    if (!this.extractedKeySections) return 0;
    return CLAUSE_SECTIONS.filter(c => {
      const s = (this.extractedKeySections as any)[c.keySectionKey] as KeySection | undefined;
      return !!(s?.found && s.language?.trim());
    }).length;
  }

  /**
   * Copy the extracted clause language onto the contract's `*Provision` fields so it shows
   * on the contract's Provisions tab. Intake does this when a contract is created; nothing
   * did it for an existing contract, so Re-analyze results were display-only.
   *
   * Fills BLANK fields only. A field that already has text is left alone, so a person's
   * edits are never overwritten. Reads the contract fresh before writing so the blank check
   * is against what is saved, not the copy the page loaded.
   */
  saveClausesToContract(): void {
    if (this.clauseSaveState === 'saving' || !this.extractedKeySections) return;
    this.clauseSaveState = 'saving';

    this.contractService.getContractById(this.data.contractId)
      .pipe(take(1))
      .subscribe(contract => {
        if (!contract) {
          this.clauseSaveState = 'error';
          return;
        }

        const filled: { [contractField: string]: string } = {};
        const added: string[] = [];
        const skipped: string[] = [];
        for (const clause of CLAUSE_SECTIONS) {
          const section = (this.extractedKeySections as any)[clause.keySectionKey] as KeySection | undefined;
          const language = section?.found ? section.language?.trim() : '';
          if (!language) continue;
          const existing = (contract as any)[clause.contractField];
          if (typeof existing === 'string' ? existing.trim() : existing) {
            skipped.push(clause.label);
            continue;
          }
          filled[clause.contractField] = language;
          added.push(clause.label);
        }

        this.clauseSaveAdded = added;
        this.clauseSaveSkipped = skipped;

        if (added.length === 0) {
          this.clauseSaveState = 'nothing-to-add';
          return;
        }

        this.contractService.writeContract({ ...contract, ...filled }, 'active', DatabaseAction.Edit)
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.clauseSaveState = 'saved';
              this.data.onClausesSaved?.(filled);
            },
            error: err => {
              console.error('[AI Analysis] failed to save clauses onto contract:', err);
              this.clauseSaveState = 'error';
            }
          });
      });
  }

  /**
   * Process the AI analysis response and extract relevant data
   */
  private processAnalysisResponse(): void {
    const response = this.data.analysisResponse;
    this.analysisError = this.data.analysisError || null;

    if (!response || response.status === 'error') {
      this.hasAIAnalysis = false;
      if (response?.error) {
        this.analysisError = response.error;
      }
      return;
    }

    if (response.status === 'complete' || response.status === 'cached') {
      this.hasAIAnalysis = true;
      this.analyzedAt = response.analyzedAt || null;

      const extraction = response.extraction;
      if (extraction) {
        // Phase 1 fields
        this.extractedParties = extraction.parties || [];
        this.extractedEffectiveDate = extraction.effectiveDate || null;
        this.extractedNoticePeriod = extraction.terminationNoticePeriod || null;

        // Phase 2 fields
        this.extractedSummary = extraction.summary || null;
        this.extractedOverview = extraction.overview || null;
        this.extractedKeySections = extraction.keySections || null;
      }

      // console.log('[AI Analysis Dialog] Analysis data loaded:', { parties: this.extractedParties, effectiveDate: this.extractedEffectiveDate, noticePeriod: this.extractedNoticePeriod, summary: this.extractedSummary, overview: this.extractedOverview, keySections: this.extractedKeySections });
    }
  }

  /**
   * Get key sections as array for template iteration
   */
  getKeySectionsArray(): { key: string; label: string; section: KeySection | undefined }[] {
    const sections: { key: string; label: string; section: KeySection | undefined }[] = [];
    const sectionKeys = Object.keys(this.keySectionLabels);

    for (const key of sectionKeys) {
      sections.push({
        key,
        label: this.keySectionLabels[key],
        section: this.extractedKeySections?.[key as keyof ExtractedKeySections]
      });
    }

    return sections;
  }

  /**
   * Format the analyzed date for display
   */
  getAnalyzedAtDisplay(): string {
    if (!this.analyzedAt) return '';
    try {
      return new Date(this.analyzedAt).toLocaleString();
    } catch {
      return this.analyzedAt;
    }
  }

  /**
   * Get confidence display class based on confidence value
   */
  getConfidenceClass(confidence: number): string {
    if (confidence >= 0.9) return 'confidence-high';
    if (confidence >= 0.7) return 'confidence-medium';
    return 'confidence-low';
  }

  /**
   * Get confidence display text
   */
  getConfidenceText(confidence: number): string {
    if (confidence >= 0.9) return 'High';
    if (confidence >= 0.7) return 'Medium';
    return 'Low';
  }

  close(): void {
    this.dialogRef.close();
  }

  getRiskScoreClass(score: string): string {
    switch (score) {
      case 'High': return 'risk-high';
      case 'Medium': return 'risk-medium';
      case 'Low': return 'risk-low';
      default: return '';
    }
  }

  async downloadDocument(): Promise<void> {
    if (this.data.document?.fetchUrl) {
      // Re-mint first: analysis dialogs stay open a while, and fetchUrl (not directLink)
      // is the only url that resolves against the private attachments bucket.
      this.data.document = await this.documentViewerService.refreshFetchUrl(this.data.document);
      this.documentViewerService.downloadFile(
        this.data.document.fetchUrl,
        this.data.document.fileName
      );
    }
  }

  async openDocumentInNewTab(): Promise<void> {
    if (this.data.document?.fetchUrl) {
      this.data.document = await this.documentViewerService.refreshFetchUrl(this.data.document);
      this.documentViewerService.openInNewTab(this.data.document.fetchUrl);
    }
  }
}
